import { Link } from "react-router-dom";
import { ArrowUpRight, Camera } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { FadeIn, MaskText, SectionTag } from "@/components/Reveal";
import Footer from "@/components/Footer";

const FEATURES = [
  {
    num: "01",
    title: "360° Swivel Seat",
    body: "Reach every angle — work from any angle without repositioning.",
  },
  {
    num: "02",
    title: "Fits Most Tubs",
    body: "Adjustable width slides to fit standard tubs from 30″ to 36″.",
  },
  {
    num: "03",
    title: "Heavy Duty",
    body: "Supports up to 250 lb — built for the jobsite, made for real work.",
  },
];

const SPEC_SLOTS = ["01", "02", "03", "04"];
const PHOTO_SLOTS = ["01", "02"];

const SitBuddyPage = () => (
  <div data-testid="sit-buddy-page" className="relative min-h-screen overflow-hidden bg-coal">
    <Seo
      title="Sit Buddy — Proxima Centauri Tools"
      siteName="Proxima Centauri Tools"
      description="Sit Buddy is the bathtub workstation from Proxima Centauri Tools — patent pending, coming soon. Turn any tub into a workstation."
      image="/sit-buddy.jpg"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "Product",
        name: "Sit Buddy",
        brand: { "@type": "Brand", name: "Proxima Centauri Tools" },
      }}
    />

    <div className="absolute inset-0" aria-hidden="true">
      <div className="absolute inset-0 bg-[#0b0b0e]" />
      <div className="brushed-metal absolute inset-0 opacity-70" />
      <div className="absolute inset-0 bg-[radial-gradient(55%_40%_at_50%_0%,rgba(216,222,232,0.12),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(45%_35%_at_50%_20%,rgba(230,0,0,0.14),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(125%_90%_at_50%_30%,transparent_55%,rgba(5,5,5,0.55)_100%)]" />
    </div>

    <PageHeader />

    <main className="relative z-10 mx-auto max-w-[1600px] px-6 pb-28 md:px-12">
      <div className="pt-14 md:pt-20">
        <SectionTag index="SB-03" label="In The Pipeline" />
        <h1 data-testid="sit-buddy-page-heading" className="mt-8 font-display font-semibold uppercase leading-[0.9] tracking-tight">
          <MaskText
            lines={[
              <span key="1" className="text-[clamp(3.6rem,10vw,10rem)] text-white">Sit</span>,
              <span key="2" className="text-[clamp(3.6rem,10vw,10rem)] metal-sheen">Buddy</span>,
            ]}
          />
        </h1>
        <FadeIn delay={0.3}>
          <p className="mt-4 font-display text-2xl uppercase tracking-[0.4em] text-ember red-glow-text md:text-3xl">
            The Bathtub Workstation
          </p>
          <p className="mt-8 max-w-2xl font-body text-base font-light leading-relaxed text-ash md:text-lg">
            Turn any tub into a workstation. Work smarter, finish faster — another product
            born on the jobsite from Proxima Centauri Tools. Full reveal coming soon.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <span
              data-testid="sit-buddy-page-patent-badge"
              className="border border-ember/60 px-5 py-2.5 font-display text-base font-semibold uppercase tracking-[0.2em] text-ember"
            >
              Patent Pending
            </span>
            <span
              data-testid="sit-buddy-page-coming-badge"
              className="bg-ember px-5 py-2.5 font-display text-base font-semibold uppercase tracking-[0.2em] text-white red-glow-box"
            >
              Coming Soon
            </span>
          </div>
        </FadeIn>
      </div>

      <FadeIn className="mt-16" delay={0.1}>
        <div
          data-testid="sit-buddy-media"
          className="relative overflow-hidden border border-white/10"
        >
          <span className="absolute left-0 top-0 z-10 h-8 w-8 border-l-2 border-t-2 border-ember" aria-hidden="true" />
          <span className="absolute bottom-0 right-0 z-10 h-8 w-8 border-b-2 border-r-2 border-ember" aria-hidden="true" />
          <img
            src="/sit-buddy.jpg"
            alt="Sit Buddy by Proxima Centauri Tools — the bathtub workstation"
            className="block h-auto w-full"
          />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          {PHOTO_SLOTS.map((slot) => (
            <div
              key={slot}
              data-testid={`sit-buddy-photo-slot-${slot}`}
              className="flex aspect-video flex-col items-center justify-center gap-3 border border-dashed border-white/15 bg-carbon/60 p-6 text-center"
            >
              <Camera size={22} className="text-ember/70" />
              <p className="font-body text-[11px] uppercase tracking-[0.3em] text-ash">
                More Photos — At Reveal
              </p>
            </div>
          ))}
        </div>
      </FadeIn>

      <div className="mt-24 grid grid-cols-1 gap-px bg-white/10 md:grid-cols-3">
        {FEATURES.map((feature, i) => (
          <FadeIn key={feature.num} delay={i * 0.12} className="bg-coal">
            <article
              data-testid={`sit-buddy-feature-${feature.num}`}
              className="group flex h-full flex-col p-10 transition-[background-color] duration-500 hover:bg-carbon"
            >
              <span className="font-display text-6xl font-semibold leading-none text-stroke-silver transition-colors duration-500 group-hover:text-ember group-hover:[-webkit-text-stroke:0px]">
                {feature.num}
              </span>
              <h2 className="mt-8 font-display text-3xl font-semibold uppercase leading-tight text-white">{feature.title}</h2>
              <p className="mt-4 font-body text-sm font-light leading-relaxed text-ash">{feature.body}</p>
            </article>
          </FadeIn>
        ))}
      </div>

      <FadeIn className="mt-24" delay={0.1}>
        <div data-testid="sit-buddy-spec-panel" className="border border-white/10 bg-carbon/60 p-8 md:p-12">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-display text-4xl font-semibold uppercase leading-none text-white md:text-5xl">
              Full Spec Sheet
            </h2>
            <p className="font-body text-[11px] uppercase tracking-[0.35em] text-ash">
              Published at the official reveal — no guesses, no hype
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {SPEC_SLOTS.map((slot) => (
              <div key={slot} data-testid={`sit-buddy-spec-slot-${slot}`} className="flex items-center justify-between bg-coal px-6 py-6">
                <span className="font-body text-xs uppercase tracking-[0.3em] text-ash">Spec {slot}</span>
                <span className="font-display text-2xl text-ember/70">—</span>
              </div>
            ))}
          </div>
        </div>
      </FadeIn>

      <FadeIn className="mt-24" delay={0.1}>
        <div className="flex flex-col items-start justify-between gap-10 border border-ember/30 bg-gradient-to-r from-ember/10 via-transparent to-transparent p-10 md:flex-row md:items-center md:p-14">
          <div>
            <h2 className="font-display text-5xl font-semibold uppercase leading-[0.95] text-white md:text-6xl">
              Be First <span className="text-ember red-glow-text">To Know.</span>
            </h2>
            <p className="mt-5 max-w-md font-body text-base font-light leading-relaxed text-ash">
              Full reveal coming soon. Send a message and we'll keep you close to the build.
            </p>
          </div>
          <Link
            to="/#contact"
            data-testid="sit-buddy-notify-cta"
            className="group relative inline-flex overflow-hidden bg-ember px-9 py-4 font-display text-xl font-semibold uppercase tracking-[0.15em] text-white transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_44px_-6px_rgba(230,0,0,0.65)]"
          >
            <span className="relative z-10 flex items-center gap-2">
              Get Notified <ArrowUpRight size={20} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </span>
            <span className="absolute inset-0 -translate-x-full bg-white/20 transition-transform duration-500 group-hover:translate-x-0" />
          </Link>
        </div>
      </FadeIn>
    </main>

    <Footer />
  </div>
);

export default SitBuddyPage;
